import { RootState } from '@/store/stores'
import { STATION_DETAIL } from '@/types/stations'
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { initialState } from './types'
import { sitesApi, stationsApi } from '@/api/operations'
import { SITE_FOR_MAPVIEW } from '@/types/site'
import { isDeletedStation, type DeletedStation } from './stationDeletionEvent'

interface StationMapResponse {
  stations: STATION_DETAIL[]
  deletionCountAtRequestStart: number
}

const keepExistingStations = (
  stations: STATION_DETAIL[],
  deletedStations: DeletedStation[],
): STATION_DETAIL[] => stations.filter(
  (station) => !deletedStations.some((deleted) => isDeletedStation(station, deleted)),
)

export const fetchStationsData = createAsyncThunk(
  'mapView/fetchStationsData',
  async (
    params: {
      interfaceIds: number[]
    },
    { getState, rejectWithValue },
  ) => {
    const deletionCountAtRequestStart =
      (getState() as RootState).mapViewSlice.deletedStations?.length ?? 0
    try {
      const responses = await Promise.all(
        params.interfaceIds.map((interfaceId) =>
          stationsApi
            .getAll(Number(interfaceId))
            .then((res) => res.data as STATION_DETAIL[])
            .catch(() => [] as STATION_DETAIL[]),
        ),
      )

      return {
        stations: responses.flat() as STATION_DETAIL[],
        deletionCountAtRequestStart,
      }
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchSiteData = createAsyncThunk(
  'mapView/fetchSiteData',
  async (
    params: {
      interfaceIds: number[]
    },
    { rejectWithValue },
  ) => {
    try {
      const responses = await Promise.all(
        params.interfaceIds.map((interfaceId) =>
          sitesApi
            .getSites(interfaceId)
            .then((res) => res.data.data as SITE_FOR_MAPVIEW[])
            .catch(() => [] as SITE_FOR_MAPVIEW[]),
        ),
      )

      return {
        siteData: responses.flat() as SITE_FOR_MAPVIEW[],
      }
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const mapViewSlice = createSlice({
  name: 'mapView',
  initialState,
  reducers: {
    removeStation(state, action: PayloadAction<DeletedStation>) {
      state.deletedStations ??= []
      state.deletedStations.push(action.payload)
      state.stations = keepExistingStations(state.stations, [action.payload])
    },
    // Merges newly lazy-loaded stations without replacing existing ones.
    mergeStations(state, action: PayloadAction<StationMapResponse>) {
      const existingKeys = new Set(
        state.stations.map((s) => `${s.id}-${s.interfaceId}`),
      )
      const deletedWhileLoading = (state.deletedStations ?? []).slice(
        action.payload.deletionCountAtRequestStart,
      )
      const incoming = keepExistingStations(action.payload.stations, deletedWhileLoading).filter(
        (s) => !existingKeys.has(`${s.id}-${s.interfaceId}`),
      )
      if (incoming.length) {
        state.stations = [...state.stations, ...incoming]
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchStationsData
      .addCase(fetchStationsData.pending, (state) => {
        state.isLoading = true
      })
      .addCase(fetchStationsData.fulfilled, (state, action) => {
        state.isLoading = false
        const deletedWhileLoading = (state.deletedStations ?? []).slice(
          action.payload.deletionCountAtRequestStart,
        )
        state.stations = keepExistingStations(action.payload.stations, deletedWhileLoading)
      })
      .addCase(fetchStationsData.rejected, (state) => {
        state.isLoading = false
      })
      // fetchSiteData
      .addCase(fetchSiteData.pending, (state) => {
        state.isLoadingSite = true
      })
      .addCase(fetchSiteData.fulfilled, (state, action) => {
        state.isLoadingSite = false
        const siteData = action.payload.siteData.map((item) => {
          if (!item.overlay) return item

          return {
            ...item,
            overlay: {
              ...item.overlay,
              id: item.overlay.id,
              interfaceId: item.overlay.interfaceId,
              siteId: item.overlay.siteId,
              bottomLeftLatitude: item.overlay.latitude,
              bottomLeftLongitude: item.overlay.longitude,
              topRightLatitude: item.overlay.latitude,
              topRightLongitude: item.overlay.longitude,
              imageUrl: item.overlay.iconUrl,
              imageFileName: item.site?.name,
              rotate: 0,
            },
          }
        })
        state.siteData = siteData
      })
      .addCase(fetchSiteData.rejected, (state) => {
        state.isLoadingSite = false
      })
  },
})

export const { mergeStations, removeStation } = mapViewSlice.actions

export const mapViewStateReducer = (state: RootState) => state.mapViewSlice

export default mapViewSlice.reducer
